import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import {
    confirmModal, hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import { useOpenModal } from '@rainbow-modules/hooks';
import Trash from 'components/icons/trash';
import ModalBody from 'components/ModalBody';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { CREATE_SIP_USER_DRAWER, EDIT_SIP_USER_DRAWER, SIP_USER_CREATED_MODAL } from '../../constants';

interface SipUser {
    id: string;
    username: string;
    sipAddress: string;
    password?: string;
    friendlyName?: string;
}

interface CreateSipUserBody {
    username: string;
    friendlyName?: string;
}

interface UpdateSipUserBody {
    friendlyName?: string;
}

const useManageSipUsers = ({
    agentId,
    domainSid,
}: {
    agentId: string;
    domainSid: string;
}) => {
    const queryClient = useQueryClient();
    const [openForm, closeForm] = useOpenModal(CREATE_SIP_USER_DRAWER);
    const [openEditForm, closeEditForm] = useOpenModal(EDIT_SIP_USER_DRAWER);
    const [openCreatedModal] = useOpenModal(SIP_USER_CREATED_MODAL);

    const { mutateAsync: createSipUser } = useHttpMutation<CreateSipUserBody, SipUser>({
        pathname: `/agents/${agentId}/sip-domains/${domainSid}/users`,
        method: 'POST',
    });

    const { mutateAsync: updateSipUser } = useHttpMutation<UpdateSipUserBody, SipUser>({
        method: 'PATCH',
    });

    const { mutateAsync: deleteSipUser } = useHttpMutation<void, void>({
        method: 'DELETE',
    });

    const handleCreate = useCallback(
        () => openForm({
            header: 'Add SIP User',
            agentId,
            domainSid,
            onSubmit: async (values: CreateSipUserBody) => {
                showAppSpinner();
                try {
                    const result = await createSipUser({ body: values }) as SipUser;
                    await queryClient.invalidateQueries(['sip-users', agentId, domainSid]);
                    closeForm();
                    hideAppSpinner();
                    openCreatedModal({
                        sipAddress: result.sipAddress,
                        password: result.password,
                    });
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                    hideAppSpinner();
                }
            },
        }),
        [agentId, domainSid, createSipUser, openForm, closeForm, queryClient, openCreatedModal],
    );

    const handleEdit = useCallback(
        (userId: string, currentFriendlyName?: string) => openEditForm({
            header: 'Edit SIP User',
            initialValues: { friendlyName: currentFriendlyName },
            onSubmit: async (values: UpdateSipUserBody) => {
                showAppSpinner();
                try {
                    await updateSipUser({
                        pathname: `/agents/${agentId}/sip-domains/${domainSid}/users/${userId}`,
                        body: values,
                    });
                    await queryClient.invalidateQueries(['sip-users', agentId, domainSid]);
                    closeEditForm();
                    showAppNotification({
                        title: 'Success',
                        description: 'SIP user updated successfully.',
                        icon: 'success',
                        timeout: 5000,
                    });
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                } finally {
                    hideAppSpinner();
                }
            },
        }),
        [agentId, domainSid, updateSipUser, openEditForm, closeEditForm, queryClient],
    );

    const handleDelete = useCallback(
        async (userId: string) => {
            const result = await confirmModal({
                header: '',
                variant: 'destructive',
                question: (
                    <ModalBody
                        variant="destructive"
                        icon={<Trash />}
                        title="Remove SIP user?"
                        description="Once this action is confirmed, it cannot be undone."
                    />
                ),
                borderRadius: 'semi-square',
                okButtonLabel: 'Remove',
                cancelButtonLabel: 'Cancel',
            });

            if (!result) return;

            showAppSpinner();
            try {
                await deleteSipUser({
                    pathname: `/agents/${agentId}/sip-domains/${domainSid}/users/${userId}`,
                });
                await queryClient.invalidateQueries(['sip-users', agentId, domainSid]);
                showAppNotification({
                    title: 'Success',
                    description: 'SIP user removed successfully.',
                    icon: 'success',
                    timeout: 5000,
                });
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [agentId, domainSid, deleteSipUser, queryClient],
    );

    return useMemo(() => ({
        create: handleCreate,
        edit: handleEdit,
        remove: handleDelete,
        closeForm,
    }), [closeForm, handleCreate, handleEdit, handleDelete]);
};

export default useManageSipUsers;
